const NUMBER = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });
const FULL_DATE = new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const SHORT_DATE = new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "short", timeZone: "UTC" });

function optionalNumber(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
    ? Number(value) : null;
}

export function formatFleetHours(minutes) {
  if (minutes === null || minutes === undefined) return "n/d";
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  return remainder ? `${hours} h ${String(remainder).padStart(2, "0")}` : `${hours} h`;
}

export function formatFleetPercent(value) {
  return value === null || value === undefined ? "n/d" : `${NUMBER.format(value)} %`;
}

export function formatFleetDate(day, short = false) {
  const value = String(day?.dispatchDate || "").slice(0, 10);
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? "Date indisponible" : (short ? SHORT_DATE : FULL_DATE).format(date);
}

export function normalizeFleetUtilization(source) {
  if (!source || typeof source !== "object") return null;
  return {
    workedMinutes: optionalNumber(source.worked_minutes),
    maxWorkMinutes: optionalNumber(source.max_work_minutes),
    retainedMinutes: optionalNumber(source.retained_minutes),
    retainedPct: optionalNumber(source.retained_utilization_pct),
    activeTruckCount: Number(source.active_truck_count) || 0,
    fullTruckCount: Number(source.full_truck_count) || 0,
    disabledTruckCount: Number(source.dispatch_disabled_truck_count) || 0,
    invalidTruckCount: Number(source.invalid_truck_count) || 0,
    trucks: (Array.isArray(source.trucks) ? source.trucks : []).map((truck) => ({
      truckId: truck.truck_id,
      name: truck.truck_name || truck.truck_id,
      workedMinutes: optionalNumber(truck.worked_minutes),
      maxWorkMinutes: optionalNumber(truck.max_work_minutes),
      retainedPct: optionalNumber(truck.retained_utilization_pct),
      overtimeMinutes: optionalNumber(truck.overtime_minutes),
    })).sort((left, right) => (
      (left.retainedPct ?? Infinity) - (right.retainedPct ?? Infinity)
      || String(left.name).localeCompare(String(right.name), "fr", { numeric: true })
    )),
  };
}

// Single source for the amber bars and the « À surveiller » list: retained utilization (90 % rule) below 50 %.
export const LOW_UTILIZATION_THRESHOLD_PCT = 50;

export function isLowUtilizationDay(day) {
  const pct = day?.fleetUtilization?.retainedPct;
  return pct !== null && pct !== undefined && pct < LOW_UTILIZATION_THRESHOLD_PCT;
}

export function hasFleetHours(data) {
  return data?.workedMinutes != null && data?.maxWorkMinutes > 0 && data?.retainedMinutes != null;
}

export function summarizeFleetUtilization(days = []) {
  const valid = days.map((day) => day.fleetUtilization).filter(hasFleetHours);
  const average = (accessor) => valid.length
    ? valid.reduce((total, data) => total + accessor(data), 0) / valid.length : null;
  const workedMinutes = average((data) => data.workedMinutes);
  const maxWorkMinutes = average((data) => data.maxWorkMinutes);
  const retainedMinutes = average((data) => data.retainedMinutes);
  const invalidTruckCount = days.reduce((total, day) => total + (day.fleetUtilization?.invalidTruckCount || 0), 0);
  const missingDayCount = days.length - valid.length;
  return {
    workedMinutes, maxWorkMinutes, retainedMinutes,
    // Both averages use the same days, so their ratio equals the ratio of cumulative hours.
    retainedPct: maxWorkMinutes > 0 ? retainedMinutes / maxWorkMinutes * 100 : null,
    validDayCount: valid.length,
    totalDayCount: days.length,
    invalidTruckCount,
    missingDayCount,
    isPartial: invalidTruckCount > 0 || missingDayCount > 0,
  };
}

export function fleetUtilizationReportFields(days) {
  const summary = summarizeFleetUtilization(days);
  return {
    fleetUtilizationWorkedMinutes: summary.workedMinutes,
    fleetUtilizationMaxWorkMinutes: summary.maxWorkMinutes,
    fleetRetainedUtilizationPct: summary.retainedPct,
    fleetUtilizationSummary: summary,
  };
}
